import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import DateInput from '../components/Forms/DateInput';
import TimeInput12 from '../components/Forms/TimeInput12';
import { normalizeCommentDeepLink } from '../../utils/commentDeepLink';
import {
  getWorkRegister,
  getWorkRegisterTeamMembers,
  updateWorkRegisterEntryStatus,
  updateWorkRegisterEntryRemarks,
  postponeWorkRegisterEntry,
  updateWorkRegisterTaskRemarks,
  updateWorkRegisterTaskStatus,
} from '../../services/WorkRegisterService';
import axiosInstance from '../../services/AxiosInstance';
import { formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import { accessControlService } from '../../services/accessControlService';
import { submenuAllowedByMyAccess } from '../../utils/routeAccessConfig';
import {
  getCurrentUserRole,
  isCorporateAdmin,
  shouldLockTeamRegisterToUserDepartment,
  shouldLockTeamRegisterBranchOnly,
} from '../../utils/roleAccess';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import ToolbarFilterInfo from '../layouts/nav/ToolbarFilterInfo';
import { convert24HourTo12 } from '../../utils/taskTime';
import { buildQuerySuffix, withReturnTo } from '../../utils/listQueryPersistence';
import '../../css/SearchFilterPanel.css';
import '../tasks/tasklist.css';
import '../Documents/commonstyle.css';
import './workRegister.css';

function isMidnightTimeValue(timeValue) {
  if (!timeValue) return true;
  const normalized = String(timeValue).slice(0, 5);
  return normalized === '00:00';
}

function formatWorkRegisterTimeRow(row) {
  const startRaw = row.start_time && !isMidnightTimeValue(row.start_time)
    ? String(row.start_time).slice(0, 5)
    : null;
  const endRaw = row.end_time && !isMidnightTimeValue(row.end_time)
    ? String(row.end_time).slice(0, 5)
    : null;

  if (startRaw && endRaw) {
    return `${convert24HourTo12(startRaw)} to ${convert24HourTo12(endRaw)}`;
  }
  if (startRaw || endRaw) {
    return convert24HourTo12(startRaw || endRaw);
  }
  return null;
}

function formatDateForApi(date) {
  if (!date) return '';
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayInputValue() {
  return formatDateForApi(new Date());
}

function getStatusBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'completed') return 'status-badge work-register-status-completed';
  if (s === 'cancelled') return 'status-badge work-register-status-cancelled';
  return 'status-badge work-register-status-pending';
}

function getDeptBranchId(dept) {
  return dept?.branch_id ?? dept?.BranchId ?? dept?.branchId;
}

function getBranchLabel(branches, branchId) {
  if (!branchId) return null;
  const branch = branches.find((b) => String(b.id) === String(branchId));
  return branch?.name || branch?.branch_name || null;
}

function getDepartmentLabel(departments, branches, deptId) {
  if (!deptId) return null;
  const dept = departments.find((d) => String(d.id) === String(deptId));
  if (!dept) return null;
  const name = dept.name || dept.department_name || 'Department';
  const branchName = getBranchLabel(branches, getDeptBranchId(dept));
  return branchName ? `${name} (${branchName})` : name;
}

function filterMembersByOrg(members, branchId, departmentId) {
  return members.filter((m) => {
    if (branchId && String(m.branch_id) !== String(branchId)) return false;
    if (departmentId && String(m.department_id) !== String(departmentId)) return false;
    return true;
  });
}


export default function WorkRegisterDashboard({ mode = 'personal' }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTeamMode = mode === 'team';
  const listBasePath = isTeamMode ? '/work-register/team' : '/work-register/list';
  const filterRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [person, setPerson] = useState(null);
  const [menuAccessKeys, setMenuAccessKeys] = useState(null);
  const [menuAccessRoutePaths, setMenuAccessRoutePaths] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    () => searchParams.get('date') || todayInputValue()
  );
  const [tempDate, setTempDate] = useState(
    () => searchParams.get('date') || todayInputValue()
  );
  const [searchDropDown, setSearchDropDown] = useState(false);
  const [searchText, setSearchText] = useState(() => searchParams.get('search') || '');

  // Keep local search in sync with navbar ?search= updates
  useEffect(() => {
    const nextSearch = searchParams.get('search') || '';
    setSearchText((prev) => (prev === nextSearch ? prev : nextSearch));
  }, [searchParams]);

  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(() => searchParams.get('user_id') || '');
  const [tempUserId, setTempUserId] = useState(() => searchParams.get('user_id') || '');
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(() => searchParams.get('branch') || '');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(() => searchParams.get('dept') || '');
  const [tempBranchId, setTempBranchId] = useState(() => searchParams.get('branch') || '');
  const [tempDepartmentId, setTempDepartmentId] = useState(() => searchParams.get('dept') || '');
  const [isBranchFilterDisabled, setIsBranchFilterDisabled] = useState(false);
  const [isDepartmentFilterDisabled, setIsDepartmentFilterDisabled] = useState(false);

  const getWorkRegisterQuerySuffix = useCallback(() => buildQuerySuffix({
    date: selectedDate || undefined,
    search: searchText || undefined,
    ...(isTeamMode ? {
      user_id: selectedUserId || undefined,
      branch: selectedBranchId || undefined,
      dept: selectedDepartmentId || undefined,
    } : {}),
  }), [
    selectedDate,
    searchText,
    isTeamMode,
    selectedUserId,
    selectedBranchId,
    selectedDepartmentId,
  ]);

  const commitWorkRegisterToUrl = useCallback(() => {
    const suffix = getWorkRegisterQuerySuffix();
    setSearchParams(new URLSearchParams(suffix.replace(/^\?/, '')), { replace: true });
  }, [getWorkRegisterQuerySuffix, setSearchParams]);

  const openItemLink = useCallback((link) => {
    if (!link) return;
    const path = normalizeCommentDeepLink(link);
    const returnPath = `${listBasePath}${getWorkRegisterQuerySuffix()}`;
    window.open(withReturnTo(path, returnPath), '_blank', 'noopener,noreferrer');
  }, [listBasePath, getWorkRegisterQuerySuffix]);

  const applyScopedOrgFilters = useCallback((members, branchId, departmentId) => {
    const scoped = filterMembersByOrg(members, branchId, departmentId);
    setSelectedUserId((prev) => (
      prev && scoped.some((m) => String(m.id) === String(prev)) ? prev : ''
    ));
    setTempUserId((prev) => (
      prev && scoped.some((m) => String(m.id) === String(prev)) ? prev : ''
    ));
    setLoading(false);
  }, []);

  const loadRoleScopedOrgFilters = useCallback(async (members) => {
    const role = getCurrentUserRole();
    if (isCorporateAdmin(role)) {
      setLoading(false);
      return { branchId: '', departmentId: '' };
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData?.id) {
      setLoading(false);
      return { branchId: '', departmentId: '' };
    }

    try {
      const userDetailsRes = await axiosInstance.get(`/auth/details/${userData.id}`);
      const userDetails = userDetailsRes.data;
      const branchId = userDetails?.Branch?.id || userDetails?.BranchId;
      const departmentId = userDetails?.Department?.id || userDetails?.DepartmentId;
      let nextBranchId = '';
      let nextDepartmentId = '';

      if (shouldLockTeamRegisterBranchOnly(role) && branchId) {
        nextBranchId = String(branchId);
        setSelectedBranchId(nextBranchId);
        setTempBranchId(nextBranchId);
        setIsBranchFilterDisabled(true);
        setIsDepartmentFilterDisabled(false);
      } else if (shouldLockTeamRegisterToUserDepartment(role)) {
        if (branchId) {
          nextBranchId = String(branchId);
          setSelectedBranchId(nextBranchId);
          setTempBranchId(nextBranchId);
          setIsBranchFilterDisabled(true);
        }
        if (departmentId) {
          nextDepartmentId = String(departmentId);
          setSelectedDepartmentId(nextDepartmentId);
          setTempDepartmentId(nextDepartmentId);
          setIsDepartmentFilterDisabled(true);
        }
      }

      applyScopedOrgFilters(members, nextBranchId, nextDepartmentId);
      return { branchId: nextBranchId, departmentId: nextDepartmentId };
    } catch (err) {
      console.error('Failed to load role-scoped org filters:', err);
      setLoading(false);
      return { branchId: '', departmentId: '' };
    }
  }, [applyScopedOrgFilters]);

  const [remarksModal, setRemarksModal] = useState({ show: false, row: null, text: '' });
  const [postponeModal, setPostponeModal] = useState({
    show: false,
    row: null,
    work_date: '',
    start_time: '',
    end_time: '',
  });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRegister = useCallback(async (date, userId) => {
    try {
      setLoading(true);
      const params = { date: formatDateForApi(date) };
      if (userId) params.user_id = userId;
      const res = await getWorkRegister(params);
      const data = res.data?.data || {};
      setItems(data.items || []);
      setPerson(data.person || null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Work Register');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    accessControlService.getMyAccess()
      .then((res) => {
        if (cancelled || !res?.data?.success) return;
        const keys = res.data?.data?.menuKeys;
        const routePaths = res.data?.data?.routePaths;
        setMenuAccessKeys(Array.isArray(keys) ? keys : []);
        setMenuAccessRoutePaths(Array.isArray(routePaths) ? routePaths : null);
      })
      .catch(() => {
        if (!cancelled) {
          setMenuAccessKeys(null);
          setMenuAccessRoutePaths(null);
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isTeamMode) return undefined;

    let cancelled = false;
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const corporateId = userData?.corporateId;

    const loadTeamData = async () => {
      try {
        const requests = [getWorkRegisterTeamMembers()];
        if (corporateId) {
          requests.push(
            axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`),
            axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`),
          );
        }

        const results = await Promise.all(requests);
        if (cancelled) return;

        const members = results[0].data?.members || [];
        setTeamMembers(members);

        if (corporateId && results.length > 2) {
          setBranches(results[1].data?.data || results[1].data || []);
          setDepartments(results[2].data?.data || results[2].data || []);
        }

        const role = getCurrentUserRole();
        const urlUserId = searchParams.get('user_id') || '';
        const urlBranch = searchParams.get('branch') || '';
        const urlDept = searchParams.get('dept') || '';
        const urlDate = searchParams.get('date') || '';

        let nextBranchId = '';
        let nextDepartmentId = '';

        if (!isCorporateAdmin(role)) {
          const scoped = await loadRoleScopedOrgFilters(members);
          nextBranchId = scoped.branchId || '';
          nextDepartmentId = scoped.departmentId || '';
        } else {
          nextBranchId = urlBranch;
          nextDepartmentId = urlDept;
          setSelectedBranchId(nextBranchId);
          setTempBranchId(nextBranchId);
          setSelectedDepartmentId(nextDepartmentId);
          setTempDepartmentId(nextDepartmentId);
          setLoading(false);
        }

        if (urlDate) {
          setSelectedDate(urlDate);
          setTempDate(urlDate);
        }

        const scopedMembers = filterMembersByOrg(members, nextBranchId, nextDepartmentId);
        if (urlUserId && scopedMembers.some((m) => String(m.id) === String(urlUserId))) {
          setSelectedUserId(urlUserId);
          setTempUserId(urlUserId);
        }
      } catch (err) {
        if (cancelled) return;
        toast.error(err.response?.data?.message || 'Failed to load team register data');
        setLoading(false);
      }
    };

    loadTeamData();

    return () => { cancelled = true; };
    // searchParams read once on team-data load; Apply Filter / URL commit keep it in sync after
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamMode, loadRoleScopedOrgFilters]);

  useEffect(() => {
    if (isTeamMode && !selectedUserId) {
      setItems([]);
      setPerson(null);
      setLoading(false);
      return;
    }
    fetchRegister(selectedDate, isTeamMode ? selectedUserId : undefined);
  }, [isTeamMode, selectedDate, selectedUserId, fetchRegister]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        if (isTeamMode && !selectedUserId) return;
        setSearchDropDown(false);
      }
    };

    if (searchDropDown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }

    return undefined;
  }, [searchDropDown, isTeamMode, selectedUserId]);

  const filteredItems = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const haystack = [
        item.description,
        item.type,
        item.remarks,
        item.status_label,
        formatWorkRegisterTimeRow(item),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [items, searchText]);

  const filteredDepartmentsForTemp = useMemo(() => {
    if (!tempBranchId) return departments;
    return departments.filter(
      (d) => String(getDeptBranchId(d)) === String(tempBranchId),
    );
  }, [departments, tempBranchId]);

  const visibleTeamMembers = useMemo(
    () => filterMembersByOrg(teamMembers, selectedBranchId, selectedDepartmentId),
    [teamMembers, selectedBranchId, selectedDepartmentId],
  );

  const tempVisibleTeamMembers = useMemo(
    () => filterMembersByOrg(teamMembers, tempBranchId, tempDepartmentId),
    [teamMembers, tempBranchId, tempDepartmentId],
  );

  const handleTempBranchChange = (branchId) => {
    setTempBranchId(branchId);
    let nextDeptId = tempDepartmentId;
    if (branchId && tempDepartmentId) {
      const dept = departments.find((d) => String(d.id) === String(tempDepartmentId));
      if (dept && String(getDeptBranchId(dept)) !== String(branchId)) {
        nextDeptId = '';
        setTempDepartmentId('');
      }
    }
    const nextMembers = filterMembersByOrg(teamMembers, branchId, nextDeptId);
    if (tempUserId && !nextMembers.some((m) => String(m.id) === String(tempUserId))) {
      setTempUserId('');
    }
  };

  const handleTempDepartmentChange = (departmentId) => {
    setTempDepartmentId(departmentId);
    const nextMembers = filterMembersByOrg(teamMembers, tempBranchId, departmentId);
    if (tempUserId && !nextMembers.some((m) => String(m.id) === String(tempUserId))) {
      setTempUserId('');
    }
  };

  const handleApplyFilter = () => {
    if (isTeamMode && !tempUserId) {
      toast.error('Please select a team member.');
      return;
    }
    setSelectedDate(tempDate);
    if (isTeamMode) {
      setSelectedBranchId(tempBranchId);
      setSelectedDepartmentId(tempDepartmentId);
      setSelectedUserId(tempUserId);
    }
    setSearchDropDown(false);
  };

  // Keep list filters in the URL so detail Back (new tab) can restore them via returnTo
  useEffect(() => {
    commitWorkRegisterToUrl();
  }, [
    selectedDate,
    selectedUserId,
    selectedBranchId,
    selectedDepartmentId,
    searchText,
    commitWorkRegisterToUrl,
  ]);

  const handleResetFilter = async () => {
    const today = todayInputValue();
    setTempDate(today);
    setSelectedDate(today);
    if (isTeamMode) {
      const role = getCurrentUserRole();
      let nextBranchId = '';
      let nextDepartmentId = '';

      if (!isCorporateAdmin(role)) {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        if (userData?.id) {
          try {
            const userDetailsRes = await axiosInstance.get(`/auth/details/${userData.id}`);
            const userDetails = userDetailsRes.data;
            const branchId = userDetails?.Branch?.id || userDetails?.BranchId;
            const departmentId = userDetails?.Department?.id || userDetails?.DepartmentId;

            if (shouldLockTeamRegisterBranchOnly(role) && branchId) {
              nextBranchId = String(branchId);
              setIsBranchFilterDisabled(true);
              setIsDepartmentFilterDisabled(false);
            } else if (shouldLockTeamRegisterToUserDepartment(role)) {
              if (branchId) {
                nextBranchId = String(branchId);
                setIsBranchFilterDisabled(true);
              }
              if (departmentId) {
                nextDepartmentId = String(departmentId);
                setIsDepartmentFilterDisabled(true);
              }
            }
          } catch (err) {
            console.error('Failed to restore role-scoped filters:', err);
          }
        }
      }

      setTempBranchId(nextBranchId);
      setSelectedBranchId(nextBranchId);
      setTempDepartmentId(nextDepartmentId);
      setSelectedDepartmentId(nextDepartmentId);
      setTempUserId('');
      setSelectedUserId('');
    }
    setSearchDropDown(isTeamMode);
  };

  const refresh = () => {
    fetchRegister(selectedDate, isTeamMode ? selectedUserId : undefined);
  };

  const handleEntryStatus = async (row, status) => {
    if (!row.entry_id) return;
    try {
      setActionLoading(true);
      await updateWorkRegisterEntryStatus(row.entry_id, status);
      toast.success(`Marked as ${status}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTaskStatus = async (row, status) => {
    if (!row.task_id) return;
    try {
      setActionLoading(true);
      await updateWorkRegisterTaskStatus(
        row.task_id,
        { status, work_date: selectedDate },
      );
      toast.success(`Marked as ${status}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePostpone = async () => {
    const row = postponeModal.row;
    if (!row?.entry_id || !postponeModal.work_date) return;
    try {
      setActionLoading(true);
      await postponeWorkRegisterEntry(row.entry_id, {
        work_date: postponeModal.work_date,
        start_time: postponeModal.start_time || null,
        end_time: postponeModal.end_time || null,
      });
      toast.success('Work Register entry postponed');
      setPostponeModal({ show: false, row: null, work_date: '', start_time: '', end_time: '' });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to postpone');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveRemarks = async () => {
    const row = remarksModal.row;
    if (!row) return;
    try {
      setActionLoading(true);
      if (row.source_type === 'comment' && row.entry_id) {
        await updateWorkRegisterEntryRemarks(row.entry_id, remarksModal.text);
      } else if (row.source_type === 'task' && row.task_id) {
        await updateWorkRegisterTaskRemarks(row.task_id, {
          work_date: selectedDate,
          remarks: remarksModal.text,
        });
      }
      toast.success('Remarks updated');
      setRemarksModal({ show: false, row: null, text: '' });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update remarks');
    } finally {
      setActionLoading(false);
    }
  };

  const openPostpone = (row) => {
    if (row.source_type === 'task') {
      openItemLink(row.link);
      return;
    }
    setPostponeModal({
      show: true,
      row,
      work_date: selectedDate,
      start_time: row.start_time ? String(row.start_time).slice(0, 5) : '',
      end_time: row.end_time ? String(row.end_time).slice(0, 5) : '',
    });
  };

  const getActions = (row) => {
    const actions = [];

    if (row.status !== 'completed') {
      actions.push({
        label: 'Mark as Completed',
        icon: 'fas fa-check',
        onClick: () => {
          if (row.source_type === 'comment') handleEntryStatus(row, 'completed');
          else handleTaskStatus(row, 'completed');
        },
      });
    }

    if (row.status !== 'cancelled' && row.status !== 'completed') {
      actions.push({
        label: 'Mark as Cancelled',
        icon: 'fas fa-times',
        onClick: () => {
          if (row.source_type === 'comment') handleEntryStatus(row, 'cancelled');
          else handleTaskStatus(row, 'cancelled');
        },
      });
    }

    actions.push({
      label: 'Update Remarks',
      icon: 'far fa-sticky-note',
      onClick: () => setRemarksModal({ show: true, row, text: row.remarks || '' }),
    });

    if (row.can_postpone) {
      if (row.status !== 'completed') {
        actions.push({
          label: 'Postpone',
          icon: 'fas fa-clock',
          onClick: () => openPostpone(row),
        });
      }
    } else {
      actions.push({
        label: 'Open Task',
        icon: 'fas fa-external-link-alt',
        onClick: () => openPostpone(row),
      });
    }

    return actions;
  };

  const columns = [
    {
      key: 'sl_no',
      header: 'Sl No',
      headerClassName: 'hide-on-mobile',
      cellClassName: 'hide-on-mobile text-center',
      render: (row) => row.sl_no,
    },
    {
      key: 'time',
      header: 'Time',
      headerClassName: 'work-register-col-time hide-on-mobile',
      cellClassName: 'work-register-col-time hide-on-mobile',
      render: (row) => formatWorkRegisterTimeRow(row) || '--',
    },
    {
      key: 'description',
      header: 'Description',
      headerClassName: 'work-register-col-description',
      cellClassName: 'work-register-col-description',
      render: (row) => {
        const timeLabel = formatWorkRegisterTimeRow(row);
        return (
        <div>
          <button
            type="button"
            className="btn btn-link p-0 text-start work-register-description-link"
            onClick={(e) => {
              e.stopPropagation();
              openItemLink(row.link);
            }}
          >
            {row.description}
          </button>
          {timeLabel && (
            <span className="work-register-time-mobile">{timeLabel}</span>
          )}
          {row.type && (
            <span className="work-register-type-subtext">{row.type}</span>
          )}
        </div>
        );
      },
    },
    {
      key: 'remarks',
      header: 'Remarks',
      headerClassName: 'hide-on-mobile',
      cellClassName: 'hide-on-mobile work-register-remarks-cell',
      render: (row) => (
        <span className="work-register-remarks-cell">{row.remarks || '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      headerClassName: 'work-register-col-status',
      cellClassName: 'work-register-col-status',
      render: (row) => (
        <div className="work-register-status-cell">
          <span className={getStatusBadgeClass(row.status)}>
            {row.status_label || row.status}
          </span>
        </div>
      ),
    },
  ];

  const displayPerson = isTeamMode && !selectedUserId ? 'Not selected' : (person?.name || '—');

  const openFilterPanel = useCallback(() => {
    setTempDate(selectedDate);
    if (isTeamMode) {
      setTempUserId(selectedUserId);
      setTempBranchId(selectedBranchId);
      setTempDepartmentId(selectedDepartmentId);
    }
    setSearchDropDown(true);
  }, [
    selectedDate,
    isTeamMode,
    selectedUserId,
    selectedBranchId,
    selectedDepartmentId,
  ]);

  const selectedBranchLabel = getBranchLabel(branches, selectedBranchId);
  const selectedDepartmentLabel = getDepartmentLabel(departments, branches, selectedDepartmentId);

  const canShowTeamRegisterLink = useMemo(() => {
    if (isTeamMode || menuAccessKeys === null) return false;
    return submenuAllowedByMyAccess(menuAccessKeys, menuAccessRoutePaths, {
      menu_key: 'work_register_team',
      to: '/work-register/team',
    });
  }, [isTeamMode, menuAccessKeys, menuAccessRoutePaths]);

  const workRegisterToolbar = useMemo(
    () => (
      <>
        <div className="page-toolbar-bar__start">
          {!searchDropDown && (
            <ToolbarFilterInfo
              ariaLabel="Register filters"
              items={[
                {
                  key: 'date',
                  icon: 'fas fa-calendar-alt',
                  label: 'Date',
                  value: formatDateToDDMMMYYYY(selectedDate),
                },
                {
                  key: 'person',
                  icon: 'fas fa-user',
                  label: 'Person',
                  value: displayPerson,
                },
                ...(isTeamMode && selectedBranchLabel
                  ? [{
                    key: 'branch',
                    icon: 'fas fa-code-branch',
                    label: 'Branch',
                    value: selectedBranchLabel,
                  }]
                  : []),
                ...(isTeamMode && selectedDepartmentLabel
                  ? [{
                    key: 'department',
                    icon: 'fas fa-building',
                    label: 'Department',
                    value: selectedDepartmentLabel,
                  }]
                  : []),
                ...(!loading
                  ? [{
                    key: 'count',
                    icon: 'fas fa-clipboard-list',
                    isCount: true,
                    value: `${filteredItems.length} item${filteredItems.length === 1 ? '' : 's'}`,
                  }]
                  : []),
              ]}
            />
          )}
        </div>
        <div className="page-toolbar-bar__end">
          {!isTeamMode && canShowTeamRegisterLink && (
            <Link
              to="/work-register/team"
              className="work-register-action-btn work-register-action-btn--nav"
              title="Team Registers"
              aria-label="Team Registers"
            >
              <i className="fas fa-users" aria-hidden />
            </Link>
          )}
          {isTeamMode && (
            <button
              type="button"
              className="work-register-action-btn work-register-action-btn--nav"
              onClick={() => navigate('/work-register/list')}
              title="My Register"
              aria-label="My Register"
            >
              <i className="fas fa-user" aria-hidden />
            </button>
          )}
          <button
            type="button"
            className="work-register-action-btn work-register-action-btn--filter"
            onClick={openFilterPanel}
            title="Filters"
            aria-label="Filters"
          >
            <i className="fas fa-filter" aria-hidden />
          </button>
        </div>
      </>
    ),
    [
      searchDropDown,
      selectedDate,
      displayPerson,
      isTeamMode,
      selectedBranchLabel,
      selectedDepartmentLabel,
      loading,
      filteredItems.length,
      canShowTeamRegisterLink,
      navigate,
      openFilterPanel,
    ]
  );

  usePageToolbar(workRegisterToolbar, [workRegisterToolbar]);

  return (
    <div className="task-list-container work-register-page">
      <ToastContainer />

      {searchDropDown && (
        <div className="search-section work-register-search-section">
          <div className="work-register-context work-register-filter-row" ref={filterRef}>
            <div className={`filter-form work-register-filter-form${isTeamMode ? ' work-register-filter-form--team' : ''}`}>
              {isTeamMode ? (
                <div className="filter-row work-register-team-filter-grid">
                  <div className="filter-group">
                    <label>Date</label>
                    <DateInput
                      type="date"
                      className="form-control work-register-filter-date"
                      value={tempDate}
                      onChange={(e) => setTempDate(e.target.value)}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Branch</label>
                    <select
                      className="form-control work-register-person-select"
                      value={tempBranchId}
                      onChange={(e) => handleTempBranchChange(e.target.value)}
                      disabled={isBranchFilterDisabled}
                    >
                      <option value="">All branches</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name || b.branch_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Department</label>
                    <select
                      className="form-control work-register-person-select"
                      value={tempDepartmentId}
                      onChange={(e) => handleTempDepartmentChange(e.target.value)}
                      disabled={isDepartmentFilterDisabled}
                    >
                      <option value="">
                        {tempBranchId ? 'All departments in branch' : 'All departments'}
                      </option>
                      {filteredDepartmentsForTemp.map((d) => (
                        <option key={d.id} value={d.id}>
                          {getDepartmentLabel(departments, branches, d.id)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>
                      Team member <span className="text-danger" aria-hidden="true">*</span>
                    </label>
                    <select
                      className="form-control work-register-person-select"
                      value={tempUserId}
                      onChange={(e) => setTempUserId(e.target.value)}
                      required
                      aria-required="true"
                    >
                      <option value="">Select team member</option>
                      {tempVisibleTeamMembers.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.full_name}
                          {m.employee_id ? ` (${m.employee_id})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Date</label>
                    <DateInput
                      type="date"
                      className="form-control work-register-filter-date"
                      value={tempDate}
                      onChange={(e) => setTempDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="filter-actions work-register-filter-actions">
              <button type="button" className="search-btn" onClick={handleApplyFilter}>
                <i className="fas fa-search me-2" aria-hidden />
                Apply
              </button>
              <button type="button" className="reset-btn" onClick={handleResetFilter}>
                <i className="fas fa-undo me-2" aria-hidden />
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      ) : (
        <CommonTable
          className="work-register-table"
          columns={columns}
          data={filteredItems}
          actions={getActions}
          hideActionsHeader
          emptyMessage={
            searchText.trim()
              ? 'No work items match your search.'
              : isTeamMode && !selectedUserId
                ? 'Select a team member from Filters to view their work register.'
              : isTeamMode && !teamMembers.length
                ? 'No team members available for your scope.'
                : isTeamMode && !visibleTeamMembers.length
                  ? 'No team members match the selected branch or department.'
                  : 'No work items for the selected date.'
          }
          emptyStateIcon="fas fa-clipboard-list"
          getRowId={(row) => row.id}
          maxHeight="85vh"
          minHeight="530px"
        />
      )}

      <Modal
        show={remarksModal.show}
        onHide={() => setRemarksModal({ show: false, row: null, text: '' })}
        centered
        dialogClassName="work-register-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Remarks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Add or update remarks for this work register item.
          </p>
          <textarea
            className="form-control"
            rows={4}
            value={remarksModal.text}
            onChange={(e) => setRemarksModal((prev) => ({ ...prev, text: e.target.value }))}
            placeholder="Add remarks for this work item…"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="doc-form-cancel-btn"
            onClick={() => setRemarksModal({ show: false, row: null, text: '' })}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button className="doc-form-submit-btn" onClick={handleSaveRemarks} disabled={actionLoading}>
            {actionLoading ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={postponeModal.show}
        onHide={() => setPostponeModal({ show: false, row: null, work_date: '', start_time: '', end_time: '' })}
        centered
        dialogClassName="work-register-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Postpone Work Register Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Choose a new date and optional time for this work register entry.
          </p>
          <div className="mb-3">
            <label className="form-label">New date</label>
            <DateInput
              type="date"
              className="form-control"
              value={postponeModal.work_date}
              onChange={(e) => setPostponeModal((prev) => ({ ...prev, work_date: e.target.value }))}
            />
          </div>
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label">Start time</label>
              <TimeInput12
                className="form-control"
                value={postponeModal.start_time}
                onChange={(e) => setPostponeModal((prev) => ({ ...prev, start_time: e.target.value }))}
              />
            </div>
            <div className="col-6">
              <label className="form-label">End time</label>
              <TimeInput12
                className="form-control"
                value={postponeModal.end_time}
                onChange={(e) => setPostponeModal((prev) => ({ ...prev, end_time: e.target.value }))}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="doc-form-cancel-btn"
            onClick={() => setPostponeModal({ show: false, row: null, work_date: '', start_time: '', end_time: '' })}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button className="doc-form-submit-btn" onClick={handlePostpone} disabled={actionLoading}>
            {actionLoading ? 'Saving…' : 'Postpone'}
          </Button>
        </Modal.Footer>
      </Modal>

      <BackToTop />
      <InternalFooter />
    </div>
  );
}
